/* ⚠️ GENERATED from app/services/vibeChatCard.ts by gen-twins.mjs — edit the TypeScript, not this. */
// ════════════════════════════════════════════════════════════════════════════
// services/vibeChatCard.ts — a Vibes post as it travels into a CHAT.
//
// 🟥 THIS FILE HAS A TWIN: allo-web/src/services/vibeChatCard.js, generated by
//    tools/gen-twins.mjs. Edit THIS file. A card written on the phone is read in
//    the browser and the other way round, off one message document with no
//    server in between to reconcile them.
//
// 🟥 AND IT EXISTS BECAUSE THE CARD WAS BUILT IN FOUR PLACES. The phone wrote it
//    three times over — the Firestore document, the optimistic local copy, and
//    the socket relay — and the browser a fourth, each an object literal typed
//    out by hand. Fields were already missing from some copies and not others,
//    which is exactly how the bug below survived: the author's PICTURE was in
//    none of them.
//
// ── WHY THE AVATAR WAS A GREY LETTER ────────────────────────────────────────
// 🟥 The bubble drew `postOwnerName.charAt(0)` on a #333 circle and called it an
//    avatar, on both clients. Not a fallback — the only thing it could do: the
//    share carried `postOwnerName` and `postOwnerId` and no image at all. So the
//    official Allo account, whose whole recognisability is a blue mark and a
//    picture, arrived in a chat as a grey "A". The blue tick was equally absent,
//    for the same reason: nothing on the card said which page had posted.
//
// ── WHAT IS A SNAPSHOT, AND WHAT IS NOT ─────────────────────────────────────
// 🟢 THE PICTURE, THE NAME AND THE TALLY ARE PAINT; THE postId IS THE TRUTH.
//    Same bargain services/sharedPost.ts strikes for feed embeds, and for the
//    same reason: a chat with thirty shared posts in it must not be thirty extra
//    reads before anything draws. An author who changes their avatar shows stale
//    on the card until someone taps through — and tapping through is one tap.
//
// 🟥 THE HONEST CONSEQUENCE, NAMED: a poll's numbers on the card are the numbers
//    at the moment it was shared. That is why the card says "Vote on post"
//    rather than offering the options as buttons — a vote cast against a stale
//    tally, in a surface that cannot hear the live one, is a vote that appears
//    to land and then disagrees with the feed.
//
// ── THE TICK IS A FACT ABOUT A PAGE, NOT A NAME ─────────────────────────────
// 🟥 Matching "Allo" by display name would hand the blue mark to anybody who
//    named their page Allo, which is the entire reason a mark exists. It is
//    `authorType === 'page' && pageId === OFFICIAL_PAGE_ID`, the same test the
//    feed card (VibesScreen ~2141), the reels player and PageProfile all use.
// ════════════════════════════════════════════════════════════════════════════
import { OFFICIAL_PAGE_ID } from './pageFollow';

/** Options kept on the card. A poll may offer six; a chat bubble that lists all
 *  six is taller than the message above and below it put together, and the card
 *  is an invitation to the post, not a replacement for it. */
export const CARD_POLL_OPTIONS = 4;

/** Caption kept on the card, in characters. The bubble clamps to three lines
 *  anyway; carrying a 2,000-character post body into every chat document is
 *  weight nobody reads. */
export const CARD_CONTENT_MAX = 400;
const s = v => v == null ? '' : String(v);

/** Is this post a poll? Deliberately the same test as services/vibePoll.ts —
 *  one answer, so no surface can disagree with another about what a poll is. */
function isPoll(post) {
  return !!post && post.type === 'poll' && Array.isArray(post.pollOptions) && post.pollOptions.length > 0;
}

/**
 * The fields a `shared_post` message carries. One object, spread by every
 * writer — the Firestore document, the optimistic copy, the socket relay and
 * the browser — so a field can never again exist on one copy and not another.
 */
export function vibeChatCard(post) {
  const p = post || {};
  const media = Array.isArray(p.media) ? p.media : [];
  /* ══ 🤖 🟥 A FIELD CALLED postImageUrl HELD AN MP4, and every renderer that
     trusted the name drew black. `p.imageUrl || p.videoUrl || media[0].url` is
     three fallbacks in a row where the second and third may be a clip, and a clip
     handed to an image decoder fails silently — a dark panel indistinguishable
     from a video that has not started. It is why a shared Allo video arrived in a
     chat as an empty bubble with a name on it.
     🟢 Two fields, each holding what it is named. A post with no still now says
        so with a null instead of lying with a URL. */
  const firstImage = media.find(m => m && m.type === 'image' && m.url);
  const firstVideo = media.find(m => m && m.type === 'video' && m.url);
  const previewImage = p.imageUrl || (firstImage ? firstImage.url : null) || null;
  const previewVideo = p.videoUrl || (firstVideo ? firstVideo.url : null) || null;
  const card = {
    postId: s(p.id),
    postContent: s(p.content).slice(0, CARD_CONTENT_MAX),
    postImageUrl: previewImage,
    // 🟢 the clip travels as a clip, so the receiver can read its own frame
    postVideoUrl: previewVideo,
    postMediaCount: media.length || (p.videoUrl ? 1 : 0) || (p.imageUrl ? 1 : 0) || 0,
    postIsVideo: !!previewVideo || p.mediaType === 'video',
    postOwnerName: s(p.userName) || '',
    postOwnerId: s(p.userId) || null,
    /* 🟢 THE THREE FIELDS THE AVATAR AND THE TICK NEEDED ALL ALONG. `postPageId`
       is what makes the mark a fact rather than a name match; `postAuthorType`
       is what distinguishes a page from a person who follows one. */
    postOwnerImage: s(p.userImage) || '',
    postAuthorType: s(p.authorType) || 'user',
    postPageId: s(p.pageId) || ''
  };
  if (p.linkUrl) {
    card.linkUrl = s(p.linkUrl);
    card.linkTitle = s(p.linkTitle);
    card.linkImage = s(p.linkImage);
    card.linkImageKind = s(p.linkImageKind);
    card.linkDomain = s(p.linkDomain);
  }
  if (isPoll(p)) {
    /* 🟥 A SHARED POLL USED TO ARRIVE AS AN EMPTY BUBBLE. `previewImage` is null
       for a poll (there is no picture), `postContent` is the question and
       nothing carried the options — so the receiver got the question alone,
       floating, with no sign it was even a poll. */
    card.postPollOptions = (p.pollOptions || []).slice(0, CARD_POLL_OPTIONS).map((o, i) => ({
      id: s(o && o.id) || `i${i}`,
      text: s(o && o.text),
      votes: Array.isArray(o && o.votes) ? o.votes.length : 0
    }));
    card.postPollTotal = Number(p.totalVotes) || 0;
    card.postPollExtra = Math.max(0, (p.pollOptions || []).length - CARD_POLL_OPTIONS);
    card.postPollClosed = !!p.pollClosed || !!p.pollClosesAt && msOf(p.pollClosesAt) > 0 && msOf(p.pollClosesAt) <= Date.now();
  }
  return card;
}

/** Every field a card puts on a message — what a FORWARD of a shared post copies.
 *  🟥 The forward copied seven of them by hand, so a forwarded share lost the author's
 *     picture and the blue mark, the link card, the poll and the clip (postVideoUrl): the
 *     same "a field on some copies and not others" this file exists to end. */
export const CARD_FIELDS = ['postId', 'postContent', 'postImageUrl', 'postVideoUrl', 'postMediaCount', 'postIsVideo', 'postOwnerName', 'postOwnerId', 'postOwnerImage', 'postAuthorType', 'postPageId', 'linkUrl', 'linkTitle', 'linkImage', 'linkImageKind', 'linkDomain', 'postPollOptions', 'postPollTotal', 'postPollExtra', 'postPollClosed'];

/** The card a shared-post message carries, as it carries it (a field it lacks stays absent). */
export function cardOf(msg) {
  const out = {};
  for (const k of CARD_FIELDS) if (msg && msg[k] !== undefined) out[k] = msg[k];
  return out;
}

/** Firestore timestamps, ISO strings and plain millis all become millis. */
function msOf(t) {
  if (!t) return 0;
  if (typeof t === 'number') return t;
  if (typeof t.toMillis === 'function') {
    try {
      return t.toMillis();
    } catch {
      return 0;
    }
  }
  if (typeof t.seconds === 'number') return t.seconds * 1000;
  const n = Date.parse(String(t));
  return Number.isNaN(n) ? 0 : n;
}

/**
 * Who the card says posted it: the picture, the letter to draw when there is no
 * picture, and whether the blue mark belongs.
 */
export function cardAuthor(msg) {
  const m = msg || {};
  const name = s(m.postOwnerName);
  return {
    name,
    image: s(m.postOwnerImage),
    /* 🟢 THE LETTER IS STILL THERE, as what it should always have been: what is
       drawn when there is no picture, not what is drawn instead of one. */
    initial: (name.trim().charAt(0) || '?').toUpperCase(),
    verified: s(m.postAuthorType) === 'page' && s(m.postPageId) === OFFICIAL_PAGE_ID
  };
}

/**
 * The poll on the card, ready to draw — or null.
 *
 * 🟢 PERCENTAGES ARE COMPUTED HERE, ONCE. Two renderers rounding independently
 *    is how a browser and a phone come to show 33% and 34% for one option.
 */
export function cardPoll(msg) {
  const m = msg || {};
  const raw = Array.isArray(m.postPollOptions) ? m.postPollOptions : null;
  if (!raw || raw.length === 0) return null;
  const options = raw.map((o, i) => ({
    id: s(o && o.id) || `i${i}`,
    text: s(o && o.text),
    votes: Number(o && o.votes) || 0,
    pct: 0
  }));
  /* 🟥 THE DENOMINATOR IS WHAT THIS CARD CAN SEE. `postPollTotal` counts votes
     cast across ALL options, including the ones past CARD_POLL_OPTIONS that the
     card is not showing — so the bars are a share of the whole poll and the
     visible ones can legitimately add up to less than 100%. Dividing by the sum
     of the visible options instead would draw a truncated poll as if it were
     the whole poll, which is a lie the reader cannot detect. */
  const total = Math.max(Number(m.postPollTotal) || 0, options.reduce((a, o) => a + o.votes, 0));
  for (const o of options) o.pct = total > 0 ? Math.round(o.votes / total * 100) : 0;
  return {
    options,
    total,
    extra: Math.max(0, Number(m.postPollExtra) || 0),
    closed: !!m.postPollClosed
  };
}

/**
 * The media on the card, disentangled — and tolerant of every card written
 * before postVideoUrl existed.
 *
 * 🟥 THOSE OLDER CARDS ARE THE REASON THIS IS A FUNCTION AND NOT A FIELD READ.
 *    Every share sent before today put the mp4 in `postImageUrl`, so a renderer
 *    that simply trusted the two fields would keep drawing those as broken
 *    images for as long as the chats exist. The extension test is the same one
 *    the bubble has always used; it is not pretty and it is the only thing that
 *    can tell a legacy card's image from its video.
 */
export function cardMedia(msg) {
  const m = msg || {};
  const declared = s(m.postVideoUrl) || null;
  const img = s(m.postImageUrl) || null;
  const looksLikeClip = !!img && /\.(mp4|mov|m4v|webm)(\?|#|$)/i.test(img);
  const legacyVideoFlag = m.postIsVideo === true || m.postIsVideo === 'true';
  const video = declared || ((legacyVideoFlag || looksLikeClip) && looksLikeClip ? img : null);
  return {
    // 🟢 a clip is never returned as the image, whichever shape the card is in
    image: img && img !== video ? img : null,
    video,
    isVideo: !!video || legacyVideoFlag,
    count: Number(m.postMediaCount) || 0
  };
}

/**
 * What kind of thing was shared — for the one-word chip on the card and for the
 * chat-list preview line. Order matters: a poll with a picture is a poll.
 */
export function cardKind(msg) {
  const m = msg || {};
  if (Array.isArray(m.postPollOptions) && m.postPollOptions.length > 0) return 'poll';
  const med = cardMedia(m);
  if (med.isVideo) return 'video';
  if (med.image) return med.count > 1 ? 'photos' : 'photo';
  if (m.linkUrl || m.postLinkUrl) return 'link';
  return 'text';
}
